import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form"; // 匯入 useForm 和 Controller
import Button from "@metacrm/metacrm-material-ui/dist/Button";
import InputField, { SIZE } from "@metacrm/metacrm-material-ui/dist/InputField";
import { Box } from "@mui/material";
import axios from "axios";
import { apiUrl } from "features/configure";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { useIntl } from "react-intl";
import { StyledRoleOptionBtn } from "./Trial.styles";
import { useOnboardingState } from "../OnboardingProvider";

export default function TrialStep1({
  nextStep,
  setTrialStep,
  stepData,
  handleStepData,
}) {
  const { formatMessage } = useIntl();
  const {
    handleSubmit,
    control,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange", // 設定為 onChange 模式，即在輸入時進行驗證
    defaultValues: {
      name: stepData.name || "",
      email: stepData.email || "",
      role: stepData.role || "",
      telegramId: stepData.telegramId || "",
    },
  });

  const { ecosystem, type } = useOnboardingState();
  const { name, email, role } = stepData;
  const [isLoading, setIsLoading] = useState(false);
  const dispatchSnackbar = useDispatchSnackbar();

  async function handleSendVerifyEmail() {
    try {
      setIsLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/api/entity/sendVerificationEmail`,
        {
          username: name,
          email,
          role,
          ecosystem: ecosystem?.toLowerCase(),
        }
      );
      handleStepData("registerId", data.registerId);
      setIsLoading(false);
      nextStep(); // 下一步
    } catch (err) {
      dispatchSnackbar({ err });
      setIsLoading(false);
    }
  }

  async function handleAddEmailRegister() {
    try {
      setIsLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/api/entity/addEmailRegister`,
        {
          username: name,
          email,
          role,
          ecosystem: ecosystem?.toLowerCase(),
        }
      );
      handleStepData("registerId", data.registerId);
      setIsLoading(false);
      setTrialStep(3);
    } catch (err) {
      dispatchSnackbar({ err });
      setIsLoading(false);
    }
  }

  // 定義表單提交的處理函數
  const onSubmit = async (data) => {
    if (!role) {
      // 如果 role 是空的，設置錯誤
      setError("role", {
        type: "manual",
        message: formatMessage({ id: "global.error.selectTeamRole" }), // 錯誤信息
      });
      return;
    }
    clearErrors("role");
    // 當表單驗證成功時，調用 nextStep
    try {
      if (type === "email") {
        await handleAddEmailRegister();
      } else {
        await handleSendVerifyEmail();
      }
    } catch (err) {
      dispatchSnackbar({ err });
    }
  };

  const roleOptions = [
    formatMessage({ id: "global.teamRole.socialMedia" }),
    formatMessage({ id: "global.teamRole.marketing" }),
    formatMessage({ id: "global.teamRole.businessDev" }),
    formatMessage({ id: "global.teamRole.sales" }),
    formatMessage({ id: "global.teamRole.customerSuccess" }),
    formatMessage({ id: "global.teamRole.dataAnalysis" }),
    formatMessage({ id: "global.teamRole.freelancer" }),
    formatMessage({ id: "global.teamRole.other" }),
  ];

  return (
    <Box
      maxWidth="442px"
      textAlign="center"
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <Box width="400px" p="0 21px">
        <Box color="#383538" fontSize="24px" fontWeight={700} mb="4px">
          {formatMessage({ id: "global.userInfo.title" })}
        </Box>
        <Box color="#777" fontSize="14px" mb="32px">
          {formatMessage({ id: "global.userInfo.subtitle" })}
        </Box>
        <Box sx={{ width: "100%" }}>
          <InputField
            name="name"
            control={control}
            value={name}
            rules={{
              required: formatMessage({ id: "global.error.requiredField" }), // 必填規則
            }}
            title={formatMessage({ id: "global.form.name.label" })}
            isRequired={true}
            placeholder="e.g. Robert Jackson"
            errors={errors.name?.message}
            onChange={(e) => {
              handleStepData("name", e.target.value); // 同時更新父組件的狀態
            }}
          />
        </Box>
        <Box height="16px" />
        {/* 使用 Controller 來包裝 Email 輸入欄位 */}
        <Box sx={{ width: "100%" }}>
          <InputField
            name="email"
            control={control}
            rules={{
              required: formatMessage({ id: "global.error.requiredField" }), // 必填規則
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: formatMessage({ id: "global.error.invalidEmail" }), // 電子郵件格式驗證
              },
            }}
            value={email}
            disabled={type === "email"}
            title={formatMessage({ id: "global.form.email.label" })}
            isRequired={true}
            placeholder="e.g. robert@gmail.com"
            errors={errors.email?.message}
            size="extraLarge"
            onChange={(e) => {
              handleStepData("email", e.target.value); // 同時更新父組件的狀態
            }}
          />
        </Box>
        <Box height="16px" />
        {/* Telegram ID 欄位 */}
        <Box sx={{ width: "100%" }}>
          <InputField
            name="telegramId"
            control={control}
            rules={{
              required: formatMessage({ id: "global.error.requiredField" }),
            }}
            value={stepData.telegramId}
            title="Telegram ID"
            isRequired={true}
            placeholder="e.g. my_telegram_id"
            errors={errors.telegramId?.message}
            onChange={(e) => {
              handleStepData("telegramId", e.target.value);
            }}
          />
        </Box>
        <Box sx={{ textAlign: "left", width: "442px", maxWidth: "442px" }}>
          <Box m="16px 0 8px">
            {formatMessage({ id: "global.form.teamRole.label" })}{" "}
            <span className="required">*</span>
          </Box>
          <Box className="item-flex" gap="12px 8px">
            {roleOptions.map((item) => (
              <StyledRoleOptionBtn
                active={item === role}
                onClick={() => {
                  handleStepData("role", item);
                  clearErrors("role");
                }}
              >
                {item}
              </StyledRoleOptionBtn>
            ))}
          </Box>
          {errors?.role && (
            <Box color="#FC5555" fontSize="14px" mt="4px">
              {errors?.role?.message}
            </Box>
          )}
        </Box>
      </Box>

      {/* 按鈕：根據表單驗證結果啟用或禁用 */}
      <Button
        onClick={handleSubmit(onSubmit)}
        sx={{ mt: "32px" }}
        isLoading={isLoading}
        disabled={!isValid || !role}
      >
        {formatMessage({ id: "global.btn.continue" })}
      </Button>
    </Box>
  );
}
